import moment from "moment-timezone";
import { FilterValue } from "@models/IFilterPresets";
import { FilterValues, IFilterField, IFilterSection, IFilterSelectOption } from "@models/IFilterConfig";

/**
 * Pure helpers for the FilterBar component. No React imports — everything here is unit-testable.
 */

/** Flatten every field across sections, preserving order. */
export function getAllFields(sections: IFilterSection[]): IFilterField[] {
    const fields: IFilterField[] = [];

    for (const section of sections) {
        for (const field of section.filters) {
            fields.push(field);
        }
    }

    return fields;
}

/** Map of field key → the section it belongs to. */
export function getFieldSectionMap(sections: IFilterSection[]): { [key: string]: IFilterSection } {
    const map: { [key: string]: IFilterSection } = {};

    for (const section of sections) {
        for (const field of section.filters) {
            map[field.key] = section;
        }
    }

    return map;
}

/** Number of filters outside exclusion sections — drives auto mode (≤3 inline, ≥4 drawer). */
export function countNonExclusionFilters(sections: IFilterSection[]): number {
    return sections.filter((s) => !s.isExclusion).reduce((total, s) => total + s.filters.length, 0);
}

/** The "inactive" value for a field. */
export function getDefaultValue(field: IFilterField): FilterValue {
    switch (field.type) {
        case "select":
            return field.options && field.options.length ? field.options[0].value : "";
        case "multiselect":
            return [];
        case "toggle":
            return false;
        case "typeahead":
        case "category":
            return field.multiple ? [] : "";
        default:
            return "";
    }
}

/** Whether a value counts as an active filter for the field (per the spec's "Considered Active When" table). */
export function isFieldActive(field: IFilterField, value: FilterValue | undefined): boolean {
    if (value === undefined || value === null) {
        return false;
    }

    switch (field.type) {
        case "select": {
            const sentinel = getDefaultValue(field);
            return String(value) !== String(sentinel);
        }
        case "multiselect":
            return Array.isArray(value) && value.length > 0;
        case "toggle":
            return value === true;
        case "typeahead":
        case "category":
            return field.multiple ? Array.isArray(value) && value.length > 0 : String(value).length > 0;
        default:
            // text, date, number
            return String(value).length > 0;
    }
}

const TRUTHY_STRINGS = ["true", "1"];

/**
 * Coerce a raw stored value (from a preset, deep link, legacy migration or defaultFilters)
 * into the canonical shape for the field type.
 */
export function normalizeValue(field: IFilterField, raw: unknown): FilterValue {
    if (raw === undefined || raw === null) {
        return getDefaultValue(field);
    }

    switch (field.type) {
        case "toggle":
            if (typeof raw === "boolean") {
                return raw;
            }
            if (typeof raw === "number") {
                return raw === 1;
            }
            return TRUTHY_STRINGS.includes(String(raw).toLowerCase());

        case "multiselect":
            return toStringArray(raw);

        case "typeahead":
        case "category":
            if (field.multiple) {
                return toStringArray(raw);
            }
            return Array.isArray(raw) ? String(raw[0] ?? "") : String(raw);

        case "select": {
            // Keep the raw value even when no option matches yet — dynamic options may arrive later.
            const asString = String(raw);
            const match = field.options?.find((o) => String(o.value) === asString);
            return match ? match.value : asString;
        }

        default:
            // text, date, number
            return String(raw);
    }
}

function toStringArray(raw: unknown): string[] {
    if (Array.isArray(raw)) {
        return raw.map((v) => String(v)).filter((v) => v.length > 0);
    }

    const asString = String(raw).trim();

    if (!asString.length) {
        return [];
    }

    return asString
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
}

/**
 * Build a complete, normalized filter state for the given sections.
 * Every field gets a value (its default when absent); keys not in `sections` are dropped.
 */
export function normalizeFilters(raw: { [key: string]: unknown } | undefined | null, sections: IFilterSection[]): FilterValues {
    const result: FilterValues = {};

    for (const field of getAllFields(sections)) {
        result[field.key] = normalizeValue(field, raw ? raw[field.key] : undefined);
    }

    return result;
}

/** Only the active fields — what gets stored in a preset. */
export function pickActive(filters: FilterValues, sections: IFilterSection[]): FilterValues {
    const result: FilterValues = {};

    for (const field of getAllFields(sections)) {
        const value = filters[field.key];

        if (isFieldActive(field, value)) {
            result[field.key] = value;
        }
    }

    return result;
}

/** Count of active fields. */
export function countActive(filters: FilterValues, sections: IFilterSection[]): number {
    return getAllFields(sections).filter((field) => isFieldActive(field, filters[field.key])).length;
}

/**
 * Serialize active filters into an API-ready query object keyed by apiKey.
 * Inactive filters (empty string, false, empty array, select at its first option) are omitted.
 * A field's optional `serialize` hook is honored when present; otherwise the raw stored value is emitted.
 */
export function buildApiQuery(filters: FilterValues, sections: IFilterSection[]): { [apiKey: string]: unknown } {
    const query: { [apiKey: string]: unknown } = {};

    for (const field of getAllFields(sections)) {
        const value = filters[field.key];

        if (!isFieldActive(field, value)) {
            continue;
        }

        query[field.apiKey] = field.serialize ? field.serialize(value) : value;
    }

    return query;
}

/** Compare two filter states field-by-field (arrays compared as sets; missing == default). */
export function areFiltersEqual(a: FilterValues | undefined, b: FilterValues | undefined, sections: IFilterSection[]): boolean {
    for (const field of getAllFields(sections)) {
        const left = normalizeValue(field, a ? a[field.key] : undefined);
        const right = normalizeValue(field, b ? b[field.key] : undefined);

        if (Array.isArray(left) || Array.isArray(right)) {
            const l = Array.isArray(left) ? [...left].sort() : [];
            const r = Array.isArray(right) ? [...right].sort() : [];

            if (l.length !== r.length || l.some((v, i) => v !== r[i])) {
                return false;
            }
        } else if (String(left) !== String(right)) {
            return false;
        }
    }

    return true;
}

/**
 * Translate URL/API params (keyed by apiKey) into filter state (keyed by field key).
 * Only params matching a configured apiKey are picked up. Used for deep links.
 */
export function filtersFromApiParams(params: { [apiKey: string]: string }, sections: IFilterSection[]): FilterValues {
    const result: FilterValues = {};

    for (const field of getAllFields(sections)) {
        const raw = params[field.apiKey];

        if (raw !== undefined && raw !== null && raw !== "") {
            result[field.key] = normalizeValue(field, raw);
        }
    }

    return result;
}

export type ChipLabelFormatter = (id: string, values?: { [key: string]: string | number }) => string;

/**
 * Human-readable value for a chip.
 * - select → matching option label
 * - multiselect / multi typeahead → single label, or "N selected"
 * - date → localized date
 * - toggle → "" (label only)
 * - typeahead / category → cached label, else the raw value
 * - text / number → the value
 */
export function getChipLabel(
    field: IFilterField,
    value: FilterValue,
    formatMessage: ChipLabelFormatter,
    optionLabels?: { [value: string]: string }
): string {
    switch (field.type) {
        case "toggle":
            return "";

        case "select": {
            const match = field.options?.find((o) => String(o.value) === String(value));
            return match ? match.label : String(value);
        }

        case "multiselect": {
            const values = Array.isArray(value) ? value : [];

            if (values.length === 1) {
                const match = field.options?.find((o) => String(o.value) === String(values[0]));
                return match ? match.label : String(values[0]);
            }

            return formatMessage("n_selected", { count: values.length });
        }

        case "typeahead":
        case "category": {
            const values = Array.isArray(value) ? value : [String(value)];

            if (values.length === 1) {
                return optionLabels?.[values[0]] || values[0];
            }

            return formatMessage("n_selected", { count: values.length });
        }

        case "date": {
            const parsed = moment(String(value), ["YYYY-MM-DD", moment.ISO_8601], true);
            return parsed.isValid() ? parsed.format("L") : String(value);
        }

        default:
            return String(value);
    }
}

/** Generate a preset id without an external dependency. */
export function newPresetId(): string {
    return `fp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Find an option by stored value. */
export function findOption(field: IFilterField, value: FilterValue): IFilterSelectOption | undefined {
    return field.options?.find((o) => String(o.value) === String(value));
}

// ── Preset tab overflow ──

/** How many preset tabs render inline before the rest move into the "More" menu. */
export const VISIBLE_PRESET_TABS = 6;

/** From this many presets (total) the "More" menu gets a search box. */
export const PRESET_SEARCH_THRESHOLD = 10;

/**
 * Split presets into the inline tabs and the "More" menu. The active preset is always inline:
 * when it would fall into the overflow it takes the last inline slot and the preset it displaced
 * moves to the front of the overflow, so stored order is otherwise preserved.
 */
export function splitPresetTabs<T extends { id: string }>(
    presets: T[],
    activePresetId: string | null,
    visibleCount: number = VISIBLE_PRESET_TABS
): { visible: T[]; overflow: T[] } {
    if (presets.length <= visibleCount) {
        return { visible: presets, overflow: [] };
    }

    const visible = presets.slice(0, visibleCount);
    const overflow = presets.slice(visibleCount);
    const activeIndex = overflow.findIndex((p) => p.id === activePresetId);

    if (activeIndex === -1) {
        return { visible, overflow };
    }

    const displaced = visible[visible.length - 1];
    const active = overflow[activeIndex];

    return {
        visible: [...visible.slice(0, -1), active],
        overflow: [displaced, ...overflow.filter((p) => p.id !== active.id)],
    };
}

/** Case-insensitive substring match on the preset name; an empty query matches everything. */
export function matchesPresetSearch(name: string, query: string): boolean {
    const q = query.trim().toLowerCase();

    return !q || name.toLowerCase().includes(q);
}

/**
 * Apply a new id order to the presets of one source, keeping the other source's presets where they
 * are. Ids not in `orderedIds` (e.g. a stale list) keep their relative position after the ordered ones.
 */
export function reorderPresetsWithin<T extends { id: string; source: "team" | "user" }>(
    presets: T[],
    source: "team" | "user",
    orderedIds: string[]
): T[] {
    const group = presets.filter((p) => p.source === source);
    const byId = new Map(group.map((p) => [p.id, p]));
    const ordered = orderedIds.map((id) => byId.get(id)).filter((p): p is T => !!p);
    const orderedSet = new Set(ordered.map((p) => p.id));
    const reordered = [...ordered, ...group.filter((p) => !orderedSet.has(p.id))];

    // Team presets always precede user presets in the merged array (see mergePresets).
    return source === "team"
        ? [...reordered, ...presets.filter((p) => p.source !== "team")]
        : [...presets.filter((p) => p.source !== "user"), ...reordered];
}
